import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ResponsiveBar } from '@nivo/bar';
import { Fetch } from '../../utils/Utils.js';
import '../../charts/reproduction/NivoChartCard.css';

// Balance positive/negative is STATE — the same good/critical steps as the
// other finance charts (FinanceCharts.js), which take them from palette.md.
const GOOD = '#0ca30c';
const CRITICAL = '#d03b3b';

// Past this many projects, show the ones with the largest balances (either
// way) so the chart stays readable; the subtitle says how many are shown.
const MAX_PROJECTS = 15;
const MAX_LABEL = 24;

const fmtSigned = (symbol, n) => `${n >= 0 ? '+' : '-'}${symbol}${Math.abs(Number(n || 0)).toFixed(2)}`;
const fmt = (symbol, n) => `${symbol}${Number(n || 0).toFixed(2)}`;
const shorten = (s) => (s.length > MAX_LABEL ? `${s.slice(0, MAX_LABEL - 1)}…` : s);

// ProjectBalancesChart — Finance → Charts (Trello FGxJ9szW): every project's
// balance side by side, most profitable at the top, losses to the left of
// zero. Clicking a bar opens that project. Same shape as FinanceCharts.js's
// "Net by institution" chart.
export const ProjectBalancesChart = ({ currencySymbol = 'R$' }) => {
  const { t }    = useTranslation();
  const navigate = useNavigate();
  const [projects,    setProjects]    = useState(null);
  const [showClosed,  setShowClosed]  = useState(false);

  useEffect(() => {
    Fetch('api/finance/projects', 'GET', null, (data) => setProjects(data || []), () => setProjects([]));
  }, []);

  const pool = (projects || []).filter(p => showClosed || p.status !== 'closed');
  const shown = [...pool]
    .sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance))
    .slice(0, MAX_PROJECTS)
    // Nivo draws horizontal bars bottom-up, so ascending puts the most
    // profitable project at the top.
    .sort((a, b) => a.balance - b.balance);

  const chartData = shown.map(p => ({
    id: p._id,
    label: `${p.code} ${shorten(p.name)}`,
    balance: Math.round(p.balance * 100) / 100,
    project: p,
  }));

  // Nivo's 'auto' scale resolves the negative extent to 0 for this shape
  // (see FinanceCharts.js "Net by institution") — pin it from the data.
  const values = chartData.map(d => d.balance).concat(0);
  const min = Math.min(...values);
  const max = Math.max(...values);

  const positive = pool.filter(p => p.balance > 0).length;
  const negative = pool.filter(p => p.balance < 0).length;
  const summary = pool.length === 0 ? '' :
    t('chart_summary_projects', { positive, negative })
    + (pool.length > MAX_PROJECTS ? ` ${t('chart_summary_projects_shown', { shown: MAX_PROJECTS, total: pool.length })}` : '');

  return (
    <div className="Chart NivoCard">
      <h3>{t('balance_by_project')}</h3>
      {summary && <p className="nivo-card-subtitle">{summary}</p>}

      <div className="nivo-toggle-row">
        <button className={`nivo-toggle-btn ${!showClosed ? 'active' : ''}`} onClick={() => setShowClosed(false)}>
          {t('project_status_active')}
        </button>
        <button className={`nivo-toggle-btn ${showClosed ? 'active' : ''}`} onClick={() => setShowClosed(true)}>
          {t('all_projects')}
        </button>
      </div>

      {projects === null ? (
        <p className="nivo-card-empty">{t('loading')}…</p>
      ) : chartData.length === 0 ? (
        <p className="nivo-card-empty">{t('no_projects')}</p>
      ) : (
        <div style={{ height: Math.max(120, chartData.length * 44) }}>
          <ResponsiveBar
            data={chartData}
            keys={['balance']}
            indexBy="label"
            layout="horizontal"
            valueScale={{ type: 'linear', min, max }}
            margin={{ top: 8, right: 36, bottom: 36, left: 190 }}
            padding={0.35}
            borderRadius={4}
            colors={(bar) => (bar.data.balance >= 0 ? GOOD : CRITICAL)}
            enableGridY={false}
            axisBottom={{ tickSize: 0, tickPadding: 8, tickValues: 5, format: (v) => `${currencySymbol}${Math.round(v)}` }}
            axisLeft={{ tickSize: 0, tickPadding: 8 }}
            label={(d) => fmtSigned(currencySymbol, d.value)}
            labelSkipWidth={64}
            labelTextColor="#ffffff"
            motionConfig="gentle"
            onClick={(bar) => navigate(`/finances/projects/${bar.data.id}`)}
            onMouseEnter={(_, e) => { e.currentTarget.style.cursor = 'pointer'; }}
            theme={{
              axis: { ticks: { text: { fill: '#898781', fontSize: 12 } } },
              grid: { line: { stroke: '#e1e0d9', strokeWidth: 1 } },
              labels: { text: { fontWeight: 700, fontSize: 11.5 } },
            }}
            tooltip={({ data }) => (
              <div className="nivo-tooltip" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                <span>
                  <span className="nivo-tooltip-swatch" style={{ background: data.balance >= 0 ? GOOD : CRITICAL, display: 'inline-block', marginRight: 6 }} />
                  <strong>{data.project.code} — {data.project.name}</strong>
                </span>
                <span>{t('project_balance')}: <span className="nivo-tooltip-value">{fmtSigned(currencySymbol, data.balance)}</span></span>
                <span>{t('project_income')}: {fmt(currencySymbol, data.project.gain)} · {t('project_expenses')}: {fmt(currencySymbol, data.project.loss)}</span>
                <span>{t('transactions')}: {data.project.transaction_count}</span>
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
};

export default ProjectBalancesChart;
